import React from 'react'

interface QuickIntegrationsProps {
  leadPhone?: string | null
  leadEmail?: string | null
  leadName: string
  onWhatsAppClick?: () => void
  onEmailClick?: () => void
}

const QuickIntegrations: React.FC<QuickIntegrationsProps> = ({
  leadPhone,
  leadEmail,
  leadName,
  onWhatsAppClick,
  onEmailClick
}) => {
  const formatPhoneForWhatsApp = (phone: string) => {
    // Remove todos os caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '')
    
    // Se não tiver código do país, adiciona 55 (Brasil)
    if (cleanPhone.length === 11) {
      return `55${cleanPhone}`
    }
    
    return cleanPhone
  }

  const handleWhatsAppClick = () => {
    if (!leadPhone) {
      alert('Lead não possui telefone cadastrado')
      return
    }

    const formattedPhone = formatPhoneForWhatsApp(leadPhone)
    const message = `Olá ${leadName}, tudo bem?`
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`
    
    // Abre em nova aba
    window.open(whatsappUrl, '_blank')
    
    if (onWhatsAppClick) {
      onWhatsAppClick()
    }
  }

  const handleEmailClick = () => {
    if (!leadEmail) {
      alert('Lead não possui email cadastrado')
      return
    }

    const subject = `Contato - ${leadName}`
    const body = `Olá ${leadName},\n\nTudo bem?\n\nAtenciosamente,`
    const mailtoUrl = `mailto:${leadEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    
    // Abre cliente de email
    window.location.href = mailtoUrl
    
    if (onEmailClick) {
      onEmailClick()
    }
  }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copiado para a área de transferência!')
    }).catch(() => {
      alert('Erro ao copiar')
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
      <div>
        <h4 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🚀</span>
          <span>Integrações Rápidas</span>
        </h4>
        
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
          Acesse rapidamente o lead via WhatsApp ou Email
        </div>

        {/* WhatsApp */}
        <div style={{ marginBottom: '12px' }}>
          <button
            onClick={handleWhatsAppClick}
            disabled={!leadPhone}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: '#25d366',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: leadPhone ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              opacity: leadPhone ? 1 : 0.5,
              transition: 'all 0.2s',
              justifyContent: 'flex-start'
            }}
            onMouseEnter={(e) => leadPhone && (e.currentTarget.style.transform = 'scale(1.02)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <span style={{ fontSize: '24px' }}>💬</span>
            <div style={{ textAlign: 'left', flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '15px' }}>WhatsApp</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {leadPhone ? leadPhone : 'Sem telefone'}
              </div>
            </div>
            <span style={{ fontSize: '20px' }}>↗</span>
          </button>
          
          {leadPhone && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyToClipboard(leadPhone)
              }}
              style={{
                marginTop: '4px',
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: 'transparent',
                color: '#666',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                margin: '8px 0 0 0'
              }}
            >
              📋 Copiar número
            </button>
          )}
        </div>

        {/* Email */}
        <div>
          <button
            onClick={handleEmailClick}
            disabled={!leadEmail}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: '#ea4335',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: leadEmail ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              opacity: leadEmail ? 1 : 0.5,
              transition: 'all 0.2s',
              justifyContent: 'flex-start'
            }}
            onMouseEnter={(e) => leadEmail && (e.currentTarget.style.transform = 'scale(1.02)')}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <span style={{ fontSize: '24px' }}>📧</span>
            <div style={{ textAlign: 'left', flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '15px' }}>Email</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                {leadEmail ? leadEmail : 'Sem email'}
              </div>
            </div>
            <span style={{ fontSize: '20px' }}>↗</span>
          </button>
          
          {leadEmail && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyToClipboard(leadEmail)
              }}
              style={{
                marginTop: '4px',
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: 'transparent',
                color: '#666',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                margin: '8px 0 0 0'
              }}
            >
              📋 Copiar email
            </button>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        padding: '12px',
        backgroundColor: '#e3f2fd',
        border: '1px solid #1976d2',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#1976d2',
        lineHeight: '1.5'
      }}>
        <strong style={{ display: 'block', marginBottom: '4px' }}>ℹ️ Informação:</strong>
        Ao clicar, você será redirecionado para o aplicativo correspondente (WhatsApp Web ou seu cliente de email). Certifique-se de ter os aplicativos instalados.
      </div>
    </div>
  )
}

export default QuickIntegrations
